// Questions fréquentes — Cabinet KEYTO
// Versus comparatifs et concepts récurrents. Structure flexible 2 à 4 colonnes.

export const FAQ = [
  // ─── COMPTABLE ────────────────────────────────────────────────────────
  {
    id: "amort-vs-deprec",
    cat: "Comptable",
    titre: "Amortissement vs Dépréciation",
    contexte: "Deux écritures qui constatent une perte de valeur, mais radicalement différentes dans leur logique.",
    cols: [
      { label: "Amortissement", color: "#2D6BE0" },
      { label: "Dépréciation", color: "#A33B4B" },
    ],
    rows: [
      { critere: "Nature de la perte", cells: ["Définitive, prévisible", "Probable, non définitive"] },
      { critere: "Réversibilité", cells: ["Irréversible", "Réversible (peut être reprise)"] },
      { critere: "Cause", cells: ["Usure, obsolescence prévue", "Événement imprévu (litige, baisse marché)"] },
      { critere: "Comptes utilisés", cells: ["`6811` / `28x`", "`6816` / `29x`, `491`, `39`"] },
      { critere: "Quand", cells: ["Chaque clôture, étalé sur durée d'utilité", "Quand un indice de perte apparaît"] },
      { critere: "Concerne", cells: ["Immos corporelles (21x) et incorporelles (20x)", "Tous actifs : immos, stocks, créances"] },
    ],
    exemple:
      "Machine 12 000 € amortie sur 5 ans → dotation annuelle 2 400 € (`6811` / `28154`). Si en N+2, sa valeur d'usage tombe à 1 000 € au lieu de 7 200 € (VNC), on enregistre une dépréciation complémentaire de 6 200 € (`6816` / `29154`).",
    memo: "L'amortissement, c'est planifié. La dépréciation, c'est subi.",
  },

  // ─── DÉCISION ─────────────────────────────────────────────────────────
  {
    id: "opex-capex-revex-finex",
    cat: "Décision",
    titre: "OPEX vs CAPEX vs REVEX vs FINEX",
    contexte: "Le vocabulaire anglo-saxon des dépenses : 4 catégories selon la nature et le cycle.",
    cols: [
      { label: "OPEX", color: "#B85724" },
      { label: "CAPEX", color: "#2D6BE0" },
      { label: "REVEX", color: "#A33B4B" },
      { label: "FINEX", color: "#3B1E6E" },
    ],
    rows: [
      {
        critere: "Signification",
        cells: ["Operating Expenditure", "Capital Expenditure", "Revenue Expenditure", "Financial Expenditure"],
      },
      {
        critere: "Nature",
        cells: ["Exploitation courante", "Investissement", "Maintenance / récurrent", "Coût du financement"],
      },
      {
        critere: "Durée d'impact",
        cells: ["Année en cours", "Plusieurs années (amorti)", "Année en cours", "Année en cours"],
      },
      {
        critere: "Compte PCG",
        cells: ["Classe `6` (60-65)", "Classe `2`", "Classes `61`-`62` (services)", "Classe `66`"],
      },
      {
        critere: "Exemples",
        cells: ["Loyers, salaires, fournitures", "Machine, logiciel, bâtiment", "Entretien, réparation, contrat de maintenance", "Intérêts d'emprunt, agios, escomptes"],
      },
      {
        critere: "Impact résultat",
        cells: ["Charge totale en N", "Étalé via amortissement", "Charge totale en N", "Charge totale en N"],
      },
    ],
    exemple:
      "Cabinet d'expertise comptable : Loyer 1 200 €/mois = OPEX (`613`). Achat d'un Mac 1 800 € = CAPEX (`2183`, amorti 3 ans). Contrat de maintenance annuel 600 € = REVEX (`6156`). Intérêts emprunt 200 €/mois = FINEX (`6611`).",
    memo: "OPEX & REVEX & FINEX = charges (classe 6). CAPEX = immobilisation (classe 2).",
  },

  {
    id: "compta-fi-vs-gestion",
    cat: "Décision",
    titre: "Comptabilité financière vs Comptabilité de gestion",
    contexte: "Deux comptabilités, deux usages, deux publics. À ne surtout pas confondre.",
    cols: [
      { label: "Comptabilité financière", color: "#15294F" },
      { label: "Comptabilité de gestion", color: "#2F7A5C" },
    ],
    rows: [
      { critere: "Aussi appelée", cells: ["Comptabilité générale", "Comptabilité analytique / managériale"] },
      { critere: "Public", cells: ["Externe : État, banques, associés, fisc", "Interne : direction, managers, opérationnels"] },
      { critere: "Obligation", cells: ["Légale (PCG, fiscale, déposable)", "Facultative (libre)"] },
      { critere: "Normes", cells: ["PCG, IFRS, normes obligatoires", "Aucune norme imposée"] },
      { critere: "Horizon", cells: ["Historique (passé)", "Prévisionnel + analytique"] },
      { critere: "Optique", cells: ["Patrimoniale et globale", "Par activité, produit, centre de coût"] },
      { critere: "Outputs", cells: ["Bilan, compte de résultat, annexe", "Coûts de revient, marges, écarts, budgets"] },
      { critere: "Périodicité", cells: ["Annuelle (clôture)", "Mensuelle, hebdo, voire temps réel"] },
    ],
    exemple:
      "Une boulangerie. Compta financière : CA total 350 k€, charges totales 320 k€, résultat 30 k€. Compta de gestion : produit pain coûte 1,20 € (revenu 1,50 €, marge 0,30 €), produit viennoiserie coûte 0,80 € (revenu 1,40 €, marge 0,60 €). La compta de gestion guide les décisions, la financière sert au reporting.",
    memo: "Financière = pour l'extérieur, obligatoire. Gestion = pour piloter, libre.",
  },

  // ─── PERFORMANCE ──────────────────────────────────────────────────────
  {
    id: "marges",
    cat: "Performance",
    titre: "Marge brute vs Marge opérationnelle vs Marge nette",
    contexte: "Trois marges pour analyser la rentabilité, à des niveaux différents du compte de résultat.",
    cols: [
      { label: "Marge brute", color: "#2D6BE0" },
      { label: "Marge opérationnelle", color: "#A56A2E" },
      { label: "Marge nette", color: "#2F7A5C" },
    ],
    rows: [
      {
        critere: "Formule",
        cells: [
          "(CA − Coût d'achat) / CA",
          "Résultat d'exploitation / CA",
          "Résultat net / CA",
        ],
      },
      {
        critere: "Aussi appelée",
        cells: ["Marge commerciale", "EBIT margin (résultat d'exploitation)", "Net margin / Bénéfice net"],
      },
      {
        critere: "Mesure",
        cells: ["Performance commerciale pure", "Performance du métier (avant finance & impôts)", "Performance globale après tout"],
      },
      {
        critere: "Comptes inclus",
        cells: ["CA (`70`) − Achats consommés (`60` + Δ stocks)", "Classes 6 et 7 d'exploitation", "Tout : exploitation + financier + exceptionnel + IS"],
      },
      {
        critere: "Bench (PME services)",
        cells: ["60 à 80 %", "10 à 25 %", "3 à 12 %"],
      },
      {
        critere: "Bench (négoce)",
        cells: ["20 à 40 %", "3 à 10 %", "1 à 5 %"],
      },
    ],
    exemple:
      "Cabinet KEYTO sur N : CA 500 k€, Achats consommés 50 k€, Charges externes 80 k€, Personnel 250 k€, Dotations 10 k€, Intérêts 5 k€, IS 25 k€. Marge brute = (500 − 50)/500 = 90 %. Marge opérationnelle = (500 − 50 − 80 − 250 − 10)/500 = 22 %. Marge nette = (500 − 50 − 80 − 250 − 10 − 5 − 25)/500 = 16 %.",
    memo: "Plus on descend dans le CR, plus on retire d'éléments → plus la marge baisse.",
  },

  // ─── COMPTES VOISINS ──────────────────────────────────────────────────
  {
    id: "706-vs-707",
    cat: "Comptes voisins",
    titre: "706 vs 707 — Prestations vs Marchandises",
    contexte: "Erreur n°1 des débutants : utiliser le mauvais compte de vente.",
    cols: [
      { label: "706 — Prestations de services", color: "#2F7A5C" },
      { label: "707 — Ventes de marchandises", color: "#2F7A5C" },
    ],
    rows: [
      { critere: "Type d'opération", cells: ["Service immatériel", "Bien physique"] },
      { critere: "Production / Revente", cells: ["Production du service", "Revente en l'état (achat-revente)"] },
      { critere: "Exemples", cells: ["Conseil, formation, abonnement, maintenance", "Distribution, e-commerce, magasin"] },
      { critere: "Lien avec stocks", cells: ["Aucun (pas de stock de service)", "Stocks de marchandises (`37`)"] },
      { critere: "Métier type", cells: ["Cabinet, agence, SaaS", "Grossiste, e-commerce, magasin"] },
    ],
    exemple:
      "KEYTO facture 5 000 € de conseil → `411` D / `706` C + `44571` C. Un magasin vend des chaussures pour 5 000 € → `411` D / `707` C + `44571` C.",
    memo: "706 = je vends ce que je produis. 707 = je revends ce que j'ai acheté.",
  },

  {
    id: "401-vs-404",
    cat: "Comptes voisins",
    titre: "401 vs 404 — Fournisseurs courants vs Immo",
    contexte: "Deux comptes de fournisseurs, à ne pas confondre selon la nature de l'achat.",
    cols: [
      { label: "401 — Fournisseurs courants", color: "#A33B4B" },
      { label: "404 — Fournisseurs d'immobilisations", color: "#A33B4B" },
    ],
    rows: [
      { critere: "Nature de l'achat", cells: ["Charge (matières, services)", "Immobilisation (matériel, logiciel)"] },
      { critere: "Contrepartie", cells: ["Compte de classe 6", "Compte de classe 2"] },
      { critere: "TVA", cells: ["`44566` (ABS)", "`44562` (immo)"] },
      { critere: "Cycle", cells: ["Court terme (30-60 j)", "Moyen-long terme parfois (crédit-bail)"] },
    ],
    exemple:
      "Achat de papier 200 € HT → `606` / `44566` / **`401`** TTC. Achat d'une machine 12 000 € HT → `2154` / `44562` / **`404`** TTC.",
    memo: "401 si contrepartie en classe 6. 404 si contrepartie en classe 2.",
  },

  {
    id: "44566-vs-44562",
    cat: "Comptes voisins",
    titre: "44566 vs 44562 — TVA déductible",
    contexte: "Deux comptes de TVA déductible : lequel utiliser ?",
    cols: [
      { label: "44566 — TVA sur ABS", color: "#A56A2E" },
      { label: "44562 — TVA sur immo", color: "#A56A2E" },
    ],
    rows: [
      { critere: "ABS = Achats Biens et Services", cells: ["Achats courants, services, charges", "—"] },
      { critere: "Achat d'immobilisation", cells: ["—", "Bien durable inscrit en classe 2"] },
      { critere: "Contrepartie typique", cells: ["Classes 60 à 65", "Classes 20, 21, 23, 26, 27"] },
      { critere: "Compte fournisseur", cells: ["`401`", "`404`"] },
    ],
    exemple:
      "Achat papeterie 100 € HT → `606` / **`44566`** / `401`. Achat ordinateur 1 000 € HT → `2183` / **`44562`** / `404`.",
    memo: "TVA suit la classe de la contrepartie : classe 2 → 44562, ailleurs → 44566.",
  },

  // ─── CLÔTURE ──────────────────────────────────────────────────────────
  {
    id: "fnp-vs-fae",
    cat: "Clôture",
    titre: "FNP vs FAE — Régularisations charges/produits",
    contexte: "Au 31/12, on rattache charges et produits au bon exercice, même sans facture.",
    cols: [
      { label: "FNP — Facture Non Parvenue", color: "#B85724" },
      { label: "FAE — Facture À Établir", color: "#2F7A5C" },
    ],
    rows: [
      { critere: "Côté", cells: ["Charge", "Produit"] },
      { critere: "Situation", cells: ["Reçu, pas encore facturé par le fournisseur", "Livré, pas encore facturé par moi"] },
      { critere: "Compte de tiers", cells: ["`408`", "`418`"] },
      { critere: "Compte de TVA", cells: ["`44586` (TVA sur FNP)", "`44587` (TVA sur FAE)"] },
      { critere: "Contrepartie", cells: ["Compte 6", "Compte 7"] },
      { critere: "Suite", cells: ["Contre-passée à l'ouverture N+1", "Contre-passée à l'ouverture N+1"] },
    ],
    exemple:
      "Marchandise reçue 28/12, facture pas arrivée : `607` D 1 200 / `44586` D 240 / `408` C 1 440. Presta livrée 30/12, facture en janvier : `418` D 3 600 / `706` C 3 000 / `44587` C 600.",
    memo: "FNP = ma charge sans facture. FAE = mon produit sans facture.",
  },

  {
    id: "cca-vs-pca",
    cat: "Clôture",
    titre: "CCA vs PCA — Charges/produits payés d'avance",
    contexte: "Quand un montant est payé d'avance mais concerne un exercice futur.",
    cols: [
      { label: "CCA — Charge Constatée d'Avance", color: "#B85724" },
      { label: "PCA — Produit Constaté d'Avance", color: "#2F7A5C" },
    ],
    rows: [
      { critere: "Côté", cells: ["Charge", "Produit"] },
      { critere: "Situation", cells: ["Charge payée en N, concerne N+1", "Produit encaissé en N, concerne N+1"] },
      { critere: "Compte", cells: ["`486` (actif)", "`487` (passif)"] },
      { critere: "Contrepartie", cells: ["Crédite la classe 6 initiale", "Débite la classe 7 initiale"] },
    ],
    exemple:
      "Loyer du T1 N+1 payé en décembre N (3 600 €) : `486` D / `613` C 3 600. Abonnement annuel encaissé pour 6 mois N+1 : `706` D 600 / `487` C 600.",
    memo: "Avance = on stocke en transitoire et on bascule l'an prochain.",
  },

  {
    id: "provision-vs-deprec",
    cat: "Clôture",
    titre: "Provision vs Dépréciation",
    contexte: "Deux mécanismes prudentiels, mais sur des objets différents.",
    cols: [
      { label: "Provision (passif)", color: "#3B1E6E" },
      { label: "Dépréciation (actif)", color: "#A33B4B" },
    ],
    rows: [
      { critere: "Concerne", cells: ["Une charge future probable", "Un actif qui perd de la valeur"] },
      { critere: "Compte", cells: ["Classe `15` (passif)", "`29x`, `39x`, `49x`, `59x`"] },
      { critere: "Exemple", cells: ["Litige prud'homal, garantie donnée", "Client douteux, stock obsolète"] },
      { critere: "Dotation", cells: ["`6815` / `15`", "`68x` / `29x`-`39x`-`49x`-`59x`"] },
    ],
    exemple:
      "Procès en cours estimé à 5 000 € → Provision : `6815` D / `1511` C 5 000. Client douteux 1 000 € HT perdu à 70 % → Dépréciation : `68174` D / `491` C 700.",
    memo: "Provision = engagement futur. Dépréciation = perte de valeur d'actif.",
  },

  // ─── TVA & ÉTATS ──────────────────────────────────────────────────────
  {
    id: "ht-vs-ttc",
    cat: "TVA",
    titre: "HT vs TTC — Où mettre quoi ?",
    contexte: "Qui est en HT, qui est en TTC dans une écriture ? Une fois la règle comprise, c'est mécanique.",
    cols: [
      { label: "HT — Hors Taxes", color: "#2D6BE0" },
      { label: "TTC — Toutes Taxes Comprises", color: "#15294F" },
    ],
    rows: [
      { critere: "Comptes concernés", cells: ["Charges (6) et Produits (7)", "Tiers (401, 411, 404)"] },
      { critere: "Logique", cells: ["C'est ma marge / mon résultat", "C'est ce que je dois ou ce qu'on me doit"] },
      { critere: "Calcul", cells: ["TTC ÷ (1 + taux TVA)", "HT × (1 + taux TVA)"] },
      { critere: "Côté écriture", cells: ["Compte de gestion", "Engagement réel"] },
    ],
    exemple:
      "Vente 1 000 € HT, TVA 20 % : `411` D 1 200 TTC / `707` C 1 000 HT / `44571` C 200 TVA.",
    memo: "Classes 6 et 7 → HT. Classe 4 (tiers) → TTC. Classe 44 → la différence.",
  },

  {
    id: "bilan-vs-cr",
    cat: "États",
    titre: "Bilan vs Compte de résultat",
    contexte: "Deux états financiers complémentaires, deux temporalités différentes.",
    cols: [
      { label: "Bilan", color: "#15294F" },
      { label: "Compte de résultat", color: "#B85724" },
    ],
    rows: [
      { critere: "Vue", cells: ["Photographie à une date", "Film d'une période"] },
      { critere: "Classes", cells: ["1 à 5", "6 et 7"] },
      { critere: "Logique", cells: ["Patrimoine : actif vs passif", "Activité : produits − charges = résultat"] },
      { critere: "Stock / Flux", cells: ["Stock (valeurs accumulées)", "Flux (mouvements de la période)"] },
      { critere: "Lien", cells: ["Reçoit le résultat (`12` au passif)", "Calcule le résultat"] },
    ],
    exemple:
      "Au 31/12 : bilan présente 200 k€ de capitaux propres, 100 k€ de dettes, 250 k€ d'actifs. CR présente 500 k€ produits − 480 k€ charges = 20 k€ bénéfice, qui rejoint les capitaux propres au bilan.",
    memo: "Bilan = ce que je possède. CR = ce que j'ai gagné/perdu.",
  },
];

export const FAQ_CATEGORIES = [...new Set(FAQ.map((f) => f.cat))];
